





















import { mapGetters } from 'vuex';
import { EStreamingType } from '@/api/schema';
import { EPermissionSuperAdmin } from '@/plugins/role-permissions/types/permission-subject';
import FeatureFlagRedirectMixin from '@/mixins/copy-stake/FeatureFlagRedirectMixin';
import { getShowCredentialsConfiguration } from '@/api/CopyStake/Settings';
import type { AxiosError } from 'axios';
import { errorToastMessage } from '@/helpers/errorToastMessage';

const CopyStakeSettingsBetAmount = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsBetAmount.vue'
  );
const CopyStakeSettingsInitialBalanceUndergroundCasino = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsInitialBalanceUndergroundCasino.vue'
  );
const CopyStakeSettingsLiveKitCredentials = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsLiveKitCredentials.vue'
  );
const CopyStakeSettingsInactivityTimeout = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsInactivityTimeout.vue'
  );
const CopyStakeSettingsLimits = (): Promise<any> =>
  import('@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsLimits.vue');
const CopyStakeSettingsAutoclickerUrl = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsAutoclickerUrl.vue'
  );
const CopyStakeSettingsShowOnlyStreamers = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsShowOnlyStreamers.vue'
  );
const CopyStakeSettingsRestream = (): Promise<any> =>
  import('@/views/CopyStake/CopyStakeSettings/CopyStakeSettingsRestream.vue');

type TSettingsComponentItem = {
  id: string;
  text: string;
  component: string;
  visible?: boolean;
  additionalData?: any;
};

export default {
  name: 'CopyStakeSettingsView',
  mixins: [FeatureFlagRedirectMixin],
  components: {
    CopyStakeSettingsBetAmount,
    CopyStakeSettingsInitialBalanceUndergroundCasino,
    CopyStakeSettingsLiveKitCredentials,
    CopyStakeSettingsAutoclickerUrl,
    CopyStakeSettingsShowOnlyStreamers,
    CopyStakeSettingsInactivityTimeout,
    CopyStakeSettingsLimits,
    CopyStakeSettingsRestream
  },
  data(): any {
    return {
      showCredentials: false,
      isLoading: false
    };
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    ...mapGetters('Onboarding', [
      'isCopyStakeGeneralIntegrationMode',
      'isCopyStakeOnFlyCopyMode',
      'isUndergroundCasinoGamesIntegrationType'
    ]),

    settingsBlock(): TSettingsComponentItem[] {
      if (!this.operatorId) return [];

      return [
        {
          id: 'BetAmountSettings',
          text: 'Bet Amount Settings',
          component: 'CopyStakeSettingsBetAmount',
          visible: this.isShowBetAmountSettings
        },
        {
          id: 'InitialBalanceUndergroundCasino',
          text: 'Initial Balance (Underground Casino)',
          component: 'CopyStakeSettingsInitialBalanceUndergroundCasino',
          visible: this.isShowInitialBalanceUndergroundCasinoSettings
        },
        {
          id: 'LiveKitCredentials',
          text: 'LiveKit Credentials',
          component: 'CopyStakeSettingsLiveKitCredentials',
          visible: this.isShowLiveKitCredentialsSettings
        },
        {
          id: 'AutoclickerUrl',
          text: 'Autoclicker URL settings',
          component: 'CopyStakeSettingsAutoclickerUrl',
          visible: this.isShowAutoclickerUrlSettings
        },
        {
          id: 'ShowOnlyStreamers',
          text: 'Show only streamers',
          component: 'CopyStakeSettingsShowOnlyStreamers',
          visible: this.isShowOnlyStreamersSettings
        },
        {
          id: 'InactivityTimeout',
          text: 'Inactivity Timeout',
          component: 'CopyStakeSettingsInactivityTimeout',
          visible: this.isShowInactivityTimeoutSettings
        },
        {
          id: 'AiStreamerLimits',
          text: 'AI Streamer Package',
          component: 'CopyStakeSettingsLimits',
          additionalData: {
            streamingType: EStreamingType.WEB_AI
          },
          visible: this.isShowLimitsSettings
        },
        {
          id: 'BasStreamerLimits',
          text: 'Become a Streamer Package',
          component: 'CopyStakeSettingsLimits',
          additionalData: {
            streamingType: EStreamingType.WEB
          },
          visible: this.isShowLimitsSettings
        },
        {
          id: 'GeneralStreamerLimits',
          text: 'General Streamer Package',
          component: 'CopyStakeSettingsLimits',
          additionalData: {
            streamingType: EStreamingType.WHIP
          },
          visible: this.isShowLimitsSettings
        },
        {
          id: 'RestreamSettings',
          text: 'Restream settings',
          component: 'CopyStakeSettingsRestream',
          visible: this.isShowRestreamSettings
        }
      ].filter(({ visible }) => visible ?? true);
    },

    isShowBetAmountSettings(): boolean {
      return (
        this.isCopyStakeGeneralIntegrationMode &&
        this.isCopyStakeOnFlyCopyMode
      );
    },
    isShowInitialBalanceUndergroundCasinoSettings(): boolean {
      return this.isUndergroundCasinoGamesIntegrationType;
    },
    isShowLiveKitCredentialsSettings(): boolean {
      return (
        this.$role.can.update(
          EPermissionSuperAdmin.SUPER_ADMIN_LIVEKIT_SETTINGS
        ) && !!this.showCredentials
      );
    },
    isShowAutoclickerUrlSettings(): boolean {
      return this.isCopyStakeGeneralIntegrationMode;
    },
    isShowOnlyStreamersSettings(): boolean {
      return (
        this.isCopyStakeGeneralIntegrationMode &&
        this.isCopyStakeOnFlyCopyMode
      );
    },
    isShowInactivityTimeoutSettings(): boolean {
      return this.isCopyStakeGeneralIntegrationMode;
    },
    isShowRestreamSettings(): boolean {
      return this.isCopyStakeGeneralIntegrationMode;
    },
    isShowLimitsSettings(): boolean {
      return this.$role.is([
        'super-admin-owner',
        'super-admin',
        'account',
        'admin',
        'copystake-account',
        'copystake-admin'
      ]);
    }
  },
  watch: {
    operatorId: {
      handler: 'getCredentialsConfiguration',
      immediate: true
    }
  },
  methods: {
    async getCredentialsConfiguration(): Promise<void> {
      if (!this.operatorId) {
        return;
      }

      this.isLoading = true;

      getShowCredentialsConfiguration()
        .then((data: boolean) => {
          this.showCredentials = data || false;
        })
        .catch((err: AxiosError): void => {
          errorToastMessage(err);
          this.showCredentials = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
